import { createContext, useState, useEffect } from "react";
import {
  primaryColor as defaultPrimary,
  textColor as defaultTextColor,
  backgroundColor as defaultBackground,
  boxShadow as defaultBoxShadow,
  border as defaultBorder,
} from "../utilities/themeColors";
import { miniStoreInfo } from "../DataApi/miniStore/miniStoreInfo";

export const MiniStoreThemeContext = createContext();

const fetchThemeFromAPI = async () => {
  try {
    const websiteInfo = await miniStoreInfo();
    // console.log("websiteInfo", websiteInfo);

    if (websiteInfo?.status === 200 && websiteInfo?.result) {
      const websiteData = websiteInfo.result; // Access it directly as it's an object

      // console.log("Raw website_palette:", websiteData.website_palette);

      let primary = defaultPrimary.primary;
      let secondary = defaultPrimary.secondary;

      // Check if website_palette is valid (not false, null, or empty)
      if (websiteData.website_palette) {
        try {
          let paletteString = websiteData.website_palette
            .replace(/'/g, '"') // Convert single quotes to double quotes
            .replace(/None/g, "null"); // Convert None to null

          const parsedPalette = JSON.parse(paletteString);
          // console.log("Parsed palette:", parsedPalette);

          primary =
            parsedPalette.find((color) => color.status === "primary")?.hex ||
            defaultPrimary.primary;
          secondary =
            parsedPalette.find((color) => color.status === "secondary")?.hex ||
            defaultPrimary.secondary;
        } catch (jsonError) {
          console.error("Error parsing website_palette JSON:", jsonError);
        }
      } else {
        console.warn(
          "website_palette is false or missing, using default colors."
        );
      }
      // console.log("Primary and Secondary Colors:", primary, secondary);
      return {
        primaryColor: { primary, secondary },
        logoUrl: websiteData.logo_url || "",
        heroImgUrl: websiteData.hero_img_url || "",
        name: websiteData.name || "",
        insta_url: websiteData.insta_url || "",
        x_url: websiteData.x_url || "",
        fb_url: websiteData.fb_url || "",
      };
    } else {
      console.warn("API response is missing required data.");
    }
  } catch (error) {
    console.error("Error fetching website data:", error);
  }
  return {
    primaryColor: {
      primary: defaultPrimary.primary,
      secondary: defaultPrimary.secondary,
    },
    logoUrl: "",
    heroImgUrl: "",
    name: "",
    insta_url: "",
    x_url: "",
    fb_url: "",
  };
};

const getContrastColor = (hex) => {
  if (!hex) return "#FFFFFF";
  const hexValue = hex.replace("#", "");
  const r = parseInt(hexValue.substring(0, 2), 16) / 255;
  const g = parseInt(hexValue.substring(2, 4), 16) / 255;
  const b = parseInt(hexValue.substring(4, 6), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

const MiniStoreThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    primaryColor: defaultPrimary,
    textColor: defaultTextColor,
    backgroundColor: defaultBackground,
    boxShadow: defaultBoxShadow,
    border: defaultBorder,
    logoUrl: "",
    heroImgUrl: "",
    name: "",
    insta_url: "",
    x_url: "",
    fb_url: "",
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const apiTheme = await fetchThemeFromAPI();

        const updatedTextColor = {
          primary: getContrastColor(apiTheme.primaryColor.primary),
          secondary: getContrastColor(apiTheme.primaryColor.secondary),
        };

        const newTheme = {
          primaryColor: apiTheme.primaryColor,
          textColor: updatedTextColor,
          backgroundColor: apiTheme.primaryColor,
          boxShadow: defaultBoxShadow,
          border: defaultBorder,
          logoUrl: apiTheme.logoUrl,
          heroImgUrl: apiTheme.heroImgUrl,
          name: apiTheme.name || "",
          insta_url: apiTheme.insta_url || "",
          x_url: apiTheme.x_url || "",
          fb_url: apiTheme.fb_url || "",
        };

        localStorage.setItem("theme", JSON.stringify(newTheme));
        setTheme(newTheme);
      } catch (error) {
        console.error("Error fetching theme:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, []);

  if (isLoading) return null;

  const updateTheme = (newTheme) => {
    setTheme((prevTheme) => ({
      ...prevTheme,
      ...newTheme,
    }));
  };

  return (
    <MiniStoreThemeContext.Provider value={{ theme, updateTheme }}>
      {children}
    </MiniStoreThemeContext.Provider>
  );
};

export default MiniStoreThemeProvider;
